import React from 'react'
import { useRouter } from 'next/router'
import Image from 'next/image'
// Components
import Button from '@/components/newButton/Button'
import Loader from '@/components/newLoader/Loader'
import Header from '@/components/newHeader/Header'
import Footer from '@/components/newFooter/newFooter'
import BasicHeadSeo from '@/components/basicHeadSeo'
// Styles
import styles from './aboutUs.module.scss'

import { ROBOTS_META } from 'const'

const AboutUs = () => {
  const router = useRouter()

  return (
    <>
      <BasicHeadSeo
        title="Learn About Mawoo Pets | MawooPets.com"
        description="Learn about Mawoo Pets, from our beginnings, to our team, customers and more. We are honored to serve you!"
        image={`${process.env.NEXT_PUBLIC_ROOT_URL}/mawoo_social_1600x906.png`}
        url={`${process.env.NEXT_PUBLIC_ROOT_URL}/about-mawoo-pets`}
        content={ROBOTS_META}
      />

      <main className={styles.main}>
        {router.isFallback && (
          <div className={styles.loader}>
            <Loader theme="tall" />
          </div>
        )}

        <Header />
        <div className={styles.content}>
          <section className={styles.sectionHeader}>
            <div className={styles.containerHeader}>
              <picture>
                <source
                  media="(max-width: 768px)"
                  srcSet="/aboutUs/img-header-mobile.png"
                />
                <Image
                  src="/aboutUs/img-header-desktop.png"
                  alt="Lady kissing her brown dachshund puppy"
                  width={1074}
                  height={250}
                  quality={100}
                  style={{ width: '100%', height: 'auto' }}
                  priority
                />
              </picture>

              <div className={styles.containerTitle}>
                <h1 className={styles.title}>About Mawoo Pets</h1>
                <h3 className={styles.subtitle}>Uniting Hearts and Paws</h3>
              </div>
            </div>
          </section>
          <div className={styles.aboutArticles}>
            <div className={styles.aboutArticleLeft}>
              <picture className={styles.articleImageDesk}>
                <Image
                  src="/aboutUs/img-article-1.png"
                  width={380}
                  height={500}
                  loading="lazy"
                  alt="The cutest chocolate Havapoo puppy walking through a field of flowers."
                />
              </picture>

              <div className={styles.text}>
                <h2>Our Vision</h2>

                <p>
                  Mawoo Pets was founded in 2021 with a simple
                  <a
                    className={styles.link}
                    href={`${process.env.NEXT_PUBLIC_ROOT_URL}/mission`}
                  >
                    {' '}
                    vision
                  </a>{' '}
                  in mind:{' '}
                  <b>
                    to create a future where every home has a pet, and every pet
                    has a home.
                  </b>{' '}
                  We observed that the world is (finally) embracing pets and
                  animals, and we want to accelerate and enable that.
                  <br />
                  Yes, the journey to find a forever companion is often fraught
                  with uncertainty and misinformation, littered with
                  untrustworthy breeders and scammers. So, we decided to bring
                  clarity and trust into the equation, building a website that
                  connects you with ethical breeders you can trust.
                  <br />
                  This way, you can not only find the perfect furry match, but
                  also be confident your new family member came from a safe,
                  loving background.
                </p>
                <h2>Team of Pet Lovers</h2>
                <p>
                  We are a team of pet lovers dedicated to creating lifelong
                  bonds between pets and their human families. Our team hails
                  form the United States, Canada, South America, Europe, and
                  Asia! This global footprint aligns with our vision of creating
                  a future for the world that has pets as a key component of it.
                </p>
              </div>

              <picture className={styles.articleImageMob}>
                <source
                  media="(max-width: 768px)"
                  srcSet="/aboutUs/img-article-1-mobile.png"
                />
                <Image
                  src="/aboutUs/img-article-1-mobile.png"
                  width={380}
                  height={200}
                  alt="The cutest chocolate Havapoo puppy walking through a field of flowers."
                  loading="lazy"
                />
              </picture>
            </div>
            <div className={styles.aboutArticleRight}>
              <div className={styles.text}>
                <h2>Thousands of Connections</h2>

                <p>
                  Today, we have grown significantly, placing thousands of
                  puppies in loving homes. Our customers come from varied
                  backgrounds and have diverse needs.
                  <br />
                  <br />
                  Whether you are a young couple looking for your first pet, or
                  a family wanting to bring home a kid-friendly puppy, or a
                  senior in search of a loyal companion, our team is here to
                  help you find and bring home the perfect match.
                </p>
                <h2>The Highest Standards and Ethics</h2>

                <p>
                  Every breeder in our network adheres to excellent health,
                  safety, and socialization practices. So you can be sure that
                  your puppy comes from a loving background, that his or her
                  parents have been ethically treated, and that you are
                  contributing to creating a caring world for animals.
                  <br />
                  All our puppies come with a range of guarantees, including a
                  health guarantee, clearance from a veterinarian, and up to
                  date on vaccination and deworming schedules.
                </p>
              </div>

              <picture className={styles.articleImageDesk}>
                <Image
                  src="/aboutUs/img-article-2.png"
                  width={380}
                  height={450}
                  alt="The cutest chocolate Havapoo puppy walking through a field of flowers."
                  loading="lazy"
                />
              </picture>

              <picture className={styles.articleImageMob}>
                <Image
                  src="/aboutUs/img-article-2-mobile.png"
                  width={380}
                  height={200}
                  style={{
                    width: '100%',
                    height: 'auto',
                  }}
                  alt="The cutest chocolate Havapoo puppy walking through a field of flowers."
                  loading="lazy"
                />
              </picture>
            </div>

            <div className={styles.aboutArticleLeft}>
              <picture className={styles.articleImageDesk}>
                <Image
                  src="/aboutUs/img-article-3.png"
                  width={380}
                  height={520}
                  alt="The cutest chocolate Havapoo puppy walking through a field of flowers."
                  loading="lazy"
                />
              </picture>

              <div className={styles.text}>
                <h2>A Lifelong Journey</h2>

                <p>
                  We understand that your journey doesn't end with bringing home
                  a new puppy. So, we go the extra mile to provide you
                  <a
                    className={styles.link}
                    href={`${process.env.NEXT_PUBLIC_ROOT_URL}/our-approach`}
                  >
                    {' '}
                    support after your puppy comes home.
                  </a>{' '}
                  This includes health guarantees for your puppy’s protection,
                  connecting you with excellent pet insurers, finding the best
                  veterinarian near you, and recommending the right brands of
                  food and toys for your new pup.
                  <br />
                  It’s a lifelong journey and we’re thrilled to be alongside you
                  for it.
                </p>
                <h2>Join Us!</h2>
                <p>
                  Our mission statement, "Creating a future where every home has
                  a pet, and every pet has a home" is not just a tagline. It is
                  the vision that fuels our innovation and dedication, pushing
                  us to consistently find new solutions for challenges that pets
                  and their owners face.
                  <br />
                  Join us and be a part of our mission to make the world a
                  better place for pets and their humans.
                </p>
                <div className={styles.btn}>
                  <Button
                    title="Browse Puppies"
                    theme={['green-solid', 'large']}
                    onClick={() => router.push('/search')}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </main>
    </>
  )
}
export default AboutUs
